import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Video, Plus, ChevronRight, CircleDot, CheckCircle2, ArchiveIcon, CalendarClock, Search, Users, ClipboardList, MessageSquare, Timer } from "lucide-react";
import CreateMeetingDialog from "@/components/CreateMeetingDialog";
import { formatDate } from "@/lib/format";

const TABS = [
  { k: "in_progress", label: "Live now", icon: CircleDot, color: "#ff4500" },
  { k: "closed", label: "Closed", icon: CheckCircle2, color: "#1a8754" },
];

const STYLE_LABEL = {
  management_review: "Monthly Management Review",
  problem_solving: "Problem Solving",
  effectiveness_review: "Effectiveness Review",
  safety: "Safety Stand-Down",
  custom: "Custom",
};

function fmtDuration(s) {
  if (!s || s < 60) return `${s || 0}s`;
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h < 1) return `${m}m`;
  return `${h}h ${m % 60}m`;
}

function monthKey(iso) {
  if (!iso) return "unscheduled";
  return iso.slice(0, 7); // "YYYY-MM"
}
function monthLabel(key) {
  if (key === "unscheduled") return "Unscheduled";
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export default function Meetings() {
  const [rows, setRows] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("in_progress");
  const [createOpen, setCreateOpen] = useState(false);
  const [q, setQ] = useState("");
  const [styleFilter, setStyleFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");

  const load = async () => {
    setBusy(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/formal-meetings", { params: { status: tab } }),
        api.get("/formal-meetings", { params: { status: "scheduled" } }),
      ]);
      setRows(r.data || []);
      setScheduled((s.data || []).sort((a, b) => (a.scheduled_for || "").localeCompare(b.scheduled_for || "")));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load meetings");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [tab]);

  // Filter + sort logic (iter28)
  const filtered = useMemo(() => {
    let list = [...rows];
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((m) =>
        (m.title || "").toLowerCase().includes(needle)
        || (m.display_id || "").toLowerCase().includes(needle)
        || STYLE_LABEL[m.style]?.toLowerCase().includes(needle)
      );
    }
    if (styleFilter !== "all") list = list.filter((m) => m.style === styleFilter);
    if (monthFilter !== "all") {
      list = list.filter((m) => monthKey(m.started_at || m.closed_at || m.created_at) === monthFilter);
    }
    list.sort((a, b) => {
      const av = a.started_at || a.closed_at || a.created_at || "";
      const bv = b.started_at || b.closed_at || b.created_at || "";
      if (sortBy === "date_asc") return av.localeCompare(bv);
      if (sortBy === "title") return (a.title || "").localeCompare(b.title || "");
      if (sortBy === "attendees") return (b.attendee_count || 0) - (a.attendee_count || 0);
      if (sortBy === "topics") return (b.topic_count || 0) - (a.topic_count || 0);
      return bv.localeCompare(av); // date_desc default
    });
    return list;
  }, [rows, q, styleFilter, monthFilter, sortBy]);

  // Distinct months for the filter dropdown
  const monthOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((m) => set.add(monthKey(m.started_at || m.closed_at || m.created_at)));
    return [...set].sort().reverse();
  }, [rows]);

  const nextNine = scheduled.slice(0, 9);

  return (
    <div className="p-4 lg:p-8 space-y-5">
      <section className="relative hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
              <Video className="w-3 h-3" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Formal meetings · audit-logged</span>
            </div>
            <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl leading-tight tracking-tight">Meetings.</h1>
            <p className="mt-2 text-sm text-white/75 max-w-md">Start a monthly review, problem-solving huddle or effectiveness check. Attendees join by email link, every note is signed and time-stamped.</p>
          </div>
          <button data-testid="meetings-create-btn" onClick={() => setCreateOpen(true)}
            className="btn-pill btn-pill-primary self-start">
            <Plus className="w-3.5 h-3.5" /> Create meeting
          </button>
        </div>
      </section>

      {/* Iter28 — Scheduled meetings 3x3 grid at the top (next 9) */}
      {nextNine.length > 0 && (
        <section data-testid="scheduled-grid" className="space-y-2">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#d6a900] font-bold">// Upcoming · already scheduled</div>
              <div className="font-display font-black text-lg text-[#1a1a1a]">Next {nextNine.length} scheduled — jump right in</div>
            </div>
            {scheduled.length > 9 && <span className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">+{scheduled.length - 9} more later</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {nextNine.map((m) => <ScheduledCard key={m.id} m={m} />)}
          </div>
        </section>
      )}

      {/* Tab + filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex bg-white border border-[#1a1a1a]/10 rounded-full p-1 shadow-soft">
          {TABS.map((t) => {
            const Icon = t.icon;
            const on = tab === t.k;
            return (
              <button key={t.k} data-testid={`meetings-tab-${t.k}`} onClick={() => setTab(t.k)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-bold ${on ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/60"}`}>
                <Icon className="w-3 h-3" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2 flex-1">
          <div className="flex items-center bg-white border border-[#1a1a1a]/10 rounded-full px-3 py-1.5 gap-1.5 shadow-soft">
            <Search className="w-3.5 h-3.5 text-[#1a1a1a]/50" />
            <input data-testid="meetings-search" placeholder="Search title, ID, style…" value={q} onChange={(e) => setQ(e.target.value)}
              className="bg-transparent outline-none text-sm w-44 placeholder:text-[#1a1a1a]/40" />
          </div>
          <select data-testid="meetings-filter-style" value={styleFilter} onChange={(e) => setStyleFilter(e.target.value)}
            className="bg-white border border-[#1a1a1a]/10 rounded-full px-3 py-1.5 text-[12px] shadow-soft">
            <option value="all">All types</option>
            {Object.entries(STYLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select data-testid="meetings-filter-month" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}
            className="bg-white border border-[#1a1a1a]/10 rounded-full px-3 py-1.5 text-[12px] shadow-soft">
            <option value="all">All months</option>
            {monthOptions.map((mk) => <option key={mk} value={mk}>{monthLabel(mk)}</option>)}
          </select>
          <select data-testid="meetings-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="bg-white border border-[#1a1a1a]/10 rounded-full px-3 py-1.5 text-[12px] shadow-soft">
            <option value="date_desc">Newest first</option>
            <option value="date_asc">Oldest first</option>
            <option value="title">Title A→Z</option>
            <option value="attendees">Most attendees</option>
            <option value="topics">Most topics</option>
          </select>
          <div className="ml-auto text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/60" data-testid="meetings-count">
            {filtered.length} of {rows.length}
          </div>
        </div>
      </div>

      {busy && <div className="text-center py-10 font-mono text-[11px] uppercase tracking-wider text-[#1a1a1a]/50">Loading…</div>}

      <div className={`grid grid-cols-1 ${tab === "closed" ? "lg:grid-cols-2 xl:grid-cols-3" : "md:grid-cols-2"} gap-4`}>
        {filtered.map((m) => <MeetingCard key={m.id} m={m} closed={tab === "closed"} />)}
        {!busy && filtered.length === 0 && (
          <div className="col-span-full bg-white border border-[#1a1a1a]/10 rounded-2xl p-10 text-center">
            <ArchiveIcon className="w-8 h-8 mx-auto text-[#1a1a1a]/30 mb-2" />
            <div className="font-display font-bold text-lg text-[#1a1a1a]/70">No {tab === "in_progress" ? "live" : "closed"} meetings match your filters.</div>
            {tab === "in_progress" && <div className="text-[12px] text-[#1a1a1a]/55 mt-1">Click <strong>Start meeting</strong> to open one now.</div>}
          </div>
        )}
      </div>

      <CreateMeetingDialog open={createOpen} onClose={() => setCreateOpen(false)}
        onCreated={(m) => { toast.success(`Meeting ${m.display_id} started`); load(); }} />
    </div>
  );
}

// Iter28 — a compact "scheduled" tile: click the whole card to open the pre-created meeting
function ScheduledCard({ m }) {
  const when = m.scheduled_for || "";
  const dateStr = when.slice(0, 10);
  const timeStr = when.slice(11, 16);
  return (
    <Link to={`/app/meetings/${m.id}`} data-testid={`scheduled-card-${m.id}`}
      className="block surface-card rounded-2xl p-4 hover:-translate-y-0.5 transition-all">
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-lg bg-[#d6a900] text-white flex items-center justify-center flex-shrink-0"><CalendarClock className="w-4 h-4" /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[9px] font-bold text-[#a37e00]">{m.display_id}</span>
            <span className="text-[9px] uppercase font-mono text-[#1a1a1a]/50">· {STYLE_LABEL[m.style] || m.style}</span>
          </div>
          <div className="font-display font-bold text-sm text-[#1a1a1a] truncate">{m.title}</div>
          <div className="text-[11px] text-[#1a1a1a]/70 mt-1 font-mono">
            {dateStr} {timeStr && <span>· {timeStr}</span>}
          </div>
          <div className="text-[10px] text-[#1a1a1a]/50 mt-1">{m.attendee_count || 0} attendee{m.attendee_count === 1 ? "" : "s"} · {m.action_count || 0} action{m.action_count === 1 ? "" : "s"}</div>
        </div>
      </div>
    </Link>
  );
}

// Iter28 — rich index card with MTG-ID, Title, Type, Date, Start, Run, #Actions, #Attendees, #Topics
function MeetingCard({ m, closed }) {
  const on = m.status === "in_progress";
  const displayDate = m.started_at || m.created_at;
  const startTime = m.started_at ? new Date(m.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "—";
  return (
    <Link to={`/app/meetings/${m.id}`} data-testid={`meeting-card-${m.id}`}
      className="block surface-card rounded-2xl hover:-translate-y-0.5 transition-all overflow-hidden">
      <div className="px-5 py-4 flex items-start gap-3 border-b border-[#1a1a1a]/06">
        <div className={`w-10 h-10 rounded-xl text-white font-display font-black flex items-center justify-center ${on ? "bg-[#ff4500]" : "bg-[#1a1a1a]"}`}>
          <Video className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-[10px] font-bold text-[#ff4500]" data-testid={`card-${m.id}-display-id`}>{m.display_id}</span>
            <span className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">· {STYLE_LABEL[m.style] || m.style}</span>
          </div>
          <div className="font-display font-bold text-base text-[#1a1a1a] truncate">{m.title}</div>
          <div className="text-[10px] font-mono text-[#1a1a1a]/55 mt-0.5">
            {formatDate(displayDate)} · Start {startTime}
            {m.run_time_seconds != null && <span> · <Timer className="w-2.5 h-2.5 inline" /> {fmtDuration(m.run_time_seconds)}</span>}
          </div>
        </div>
        {on && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-white bg-[#ff4500] px-2 py-0.5 rounded-full animate-pulse">
          <CircleDot className="w-2.5 h-2.5" /> Live
        </span>}
      </div>
      <div className="px-5 py-3 grid grid-cols-3 gap-3">
        <StatCol icon={ClipboardList} label="Actions" value={m.action_count} tint="#ff4500" />
        <StatCol icon={Users} label="Attendees" value={m.attendee_count} tint="#1a1a1a" />
        <StatCol icon={MessageSquare} label="Topics" value={m.topic_count} tint="#d6a900" />
      </div>
      {closed && (m.actions_created_count > 0 || m.notes_count > 0) && (
        <div className="px-5 pb-3 flex flex-wrap gap-1.5">
          {m.actions_created_count > 0 && <span className="text-[9px] font-mono uppercase font-bold text-white bg-[#ff4500] px-2 py-0.5 rounded-full">+{m.actions_created_count} actions created</span>}
          {m.notes_count > 0 && <span className="text-[9px] font-mono uppercase font-bold text-[#1a1a1a] bg-[#1a1a1a]/10 px-2 py-0.5 rounded-full">{m.notes_count} notes</span>}
        </div>
      )}
      <div className="px-5 py-2 border-t border-[#1a1a1a]/06 text-[11px] uppercase tracking-wider font-bold text-[#ff4500] hover:text-[#cc3500] inline-flex items-center gap-1 w-full">
        {on ? "Join meeting" : "View minutes"} <ChevronRight className="w-3 h-3" />
      </div>
    </Link>
  );
}

function StatCol({ icon: Icon, label, value, tint }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tint}15`, color: tint }}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="font-mono text-[9px] uppercase tracking-wider text-[#1a1a1a]/55">{label}</div>
        <div className="font-display font-black text-sm text-[#1a1a1a]">{value ?? 0}</div>
      </div>
    </div>
  );
}
